/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Flatpickr from "react-flatpickr";
import { Row, Col } from "react-bootstrap";
import Popover from "react-bootstrap/Popover";
import moment from "helpers/moment";
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt';
import "moment/locale/de";
import "moment/locale/it";
import "moment/locale/ja";
import "moment/locale/zh-cn";
import "moment/locale/cs";
import "moment/locale/nb";
import "moment/locale/sv";
import "moment/locale/ko";
import "moment/locale/ar";
import "moment/locale/pl";
import 'moment/locale/pt-br';

/* Helpers, constants, custom methods, internal libraries */
import UCOutsideClickHandler from "../ucOusideClickHandler/UCOutsideClickHandler";
import { getCalendarLocale } from '../../helpers/general';

/* Components */
import {ReactComponent as CalendarIcon} from "../../resources/images/calendar.svg";
import { useIsMobile } from "../../hooks/useIsMobile";
import UCButton from "../buttons";
import { useLocaleContext } from "../../contexts/localeContext";

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  safetySelectDates: {
    id: "safety.filters.selectDates"
  },
})

const SelectDates = ({gridOptions, setGridOptions}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const isMobile = useIsMobile();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  const [startDate, setStartDate] = useState(gridOptions?.startDate && gridOptions?.type === "custom" ? gridOptions?.startDate : null)
  const [endDate, setEndDate] = useState(gridOptions?.endDate && gridOptions?.type === "custom" ? gridOptions?.endDate : null)
  const [showOverlay, setShowOverlay] = useState(false)
  const dateFormat = "MMM DD, YYYY";
  const globalLocale = useLocaleContext();

  useEffect(() => {
    if (gridOptions?.type !== "custom") {
      setStartDate(null)
      setEndDate(null)
    }
  }, [gridOptions])

  const applyDates = () => {
    setGridOptions({
      ...gridOptions,
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      type: "custom",
      page: 1
    })
    setShowOverlay(false)
  }

  const getPlaceholder = () => {
    if (gridOptions?.startDate && gridOptions?.endDate && gridOptions?.type === "custom") {

      if (moment(gridOptions?.startDate).isSame(gridOptions?.endDate)){
        return `${moment(gridOptions?.startDate).locale(globalLocale).format(dateFormat)}`
      } else {
        return `${moment(gridOptions?.startDate).locale(globalLocale).format(dateFormat)} - ${moment(gridOptions?.endDate).locale(globalLocale).format(dateFormat)}`;
      }
    }
    return formatMessage(messages.safetySelectDates)
  }

  const renderPopover = () => {
    return(
      <Popover className={classes.popoverStyle} tabIndex={0} aria-label={intl.formatMessage({id: "bookings.popoverAriaLabel"})}>
        <UCOutsideClickHandler onOutsideClick = {() => {setShowOverlay(false)}}  >
        <div className={classes.popoverDiv}>
          <div className={classes.popoverContainer}>
            <div className={classes.flat}>
              <Flatpickr
                options={{
                  mode: "range",
                  inline: true,
                  dateFormat: "Y-m-d",
                  showMonths: isMobile ? "1" : "2",
                  // dateFormat: "m/d/Y",
                  //minDate: "",
                  //maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
                  defaultDate: [startDate, endDate],
                  locale: {
                    ...getCalendarLocale(globalLocale),
                    firstDayOfWeek: 1, // start week on Monday,
                  },
                  onReady: (selectedDates, dateStr, instance) => {
                    const calendar = instance.calendarContainer;
                    calendar.setAttribute("tabIndex", "0");
                  },
                }}
                onChange={(date) => {
                  setStartDate(date[0])
                  setEndDate(date[1] ? date[1] : null)
                  document.getElementById("selected-dates").focus()
                }}
              />
              <div className={classes.divider}></div>
            </div>
            <Row className={classes.footer}>
              <Col xs={12} sm={12} md={6} lg={6} className={classes.selectedDates} id="selected-dates" tabIndex={startDate ? 0 : -1}>
                {
                  startDate &&
                  <>
                    <div className={classes.selectedDate}>
                      { startDate && moment(startDate).locale(globalLocale).format(dateFormat) }
                    </div>
                    <div className={classes.separator}>
                      -
                    </div>
                    <div className={classes.selectedDate}>
                      { endDate && moment(endDate).locale(globalLocale).format(dateFormat) }
                    </div>
                  </>
                }
              </Col>
              <Col xs={12} sm={12} md={12} lg={6} className={classes.actions}>
                <UCButton
                  onClick={() => {
                    setStartDate(gridOptions?.startDate && gridOptions?.type === "custom" ? gridOptions?.startDate : null);
                    setEndDate(gridOptions?.endDate && gridOptions?.type === "custom" ? gridOptions?.endDate : null);
                    setShowOverlay(false);
                  }}
                  type="default"
                  className={classes.cancel}
                  text={<FormattedMessage id="safety.filters.cancel" />}
                  tabIndex={0}
                />
                <UCButton
                  onClick={applyDates}
                  type="primary"
                  disabled={!(startDate && endDate)}
                  className={classes.apply}
                  text={<FormattedMessage id="safety.filters.apply" />}
                  tabIndex={0}
                />
              </Col>
            </Row>
          </div>
        </div>
        </UCOutsideClickHandler>
      </Popover>
    )
  }

  return (
    <OverlayTrigger show={showOverlay} trigger="click" rootClose={true} placement="bottom" overlay={renderPopover()} onEntered={(node) => node.focus()}>
      <div>
        <UCButton
          onClick={() => setShowOverlay(true)}
          type="default"
          className={`${classes.filter} ${classes.selectDates} ${gridOptions?.type === "custom" ? classes.active : ""}`}
          iconClassName={classes.icon}
          text={getPlaceholder()}
          ariaLabel={getPlaceholder()}
          tabIndex={0}
          icon={
            <CalendarIcon />
          }
        />
      </div>
    </OverlayTrigger>
  )
}

export default SelectDates;
